/**
 * local storage keys
 */
export enum STORAGE_KEY {
    /**
     * 设置已读的用户成就弹窗
     */
    UN_READ_DIALOG_REQUEST = 'un_read_dialog_request',

    /**
     * 设置获取课程学习证书是否已经弹窗的标记
     */
    READ_DIALOG_COURSE_CERTIFICATE = 'read_dialog_course_certificate',
    /**
     * 设置获取学习项目学习证书是否已经弹窗的标记
     */
    READ_DIALOG_PROGRAM_CERTIFICATE = 'read_dialog_program_certificate',

    /**
     * 保存区号
     */
    AREA_CODE = 'areaCode',

    /**
     * 登录用户ID
     */
    LOGIN_USER_ID = 'LOGIN_USER_ID',

    /**
     * 语音微课播放进度
     */
    AUDIO_PLAY_PROGRESS = 'AUDIO_PLAY_PROGRESS',

    /**
     * 提问讨论匿名时保存的临时用户名
     */
    POST_NAME = 'UName',

    /**
     * 直播编辑页面的引导提示浮层是否已读
     */
    LIVE_EDIT_USAGE_GUIDE_READED = 'LIVE_EDIT_USAGE_GUIDE_READED',

    /**
     * 发言框发送内容
     */
    COMMENT_POST_VALUE = 'COMMENT_POST_VALUE',

    /**
     * 发言框最后一次发送时间
     */
    COMMENT_POST_LAST_TIME = 'COMMENT_POST_LAST_TIME',

    /**
     * 判断用户是否收藏
     */
    SET_IS_COLLECT_STATE = 'SET_IS_COLLECT_STATE',

    /**
     * 是否第一次加入考题本
     */
    IS_FIRST_QUESTION_COLLECTION = 'IS_FIRST_QUESTION_COLLECTION',

    /**
     * 成员是否第一次进入学习圈
     */
    IS_MEMBER_FIRST_LEARNING_CRICLE = 'IS_MEMBER_FIRST_LEARNING_CRICLE',

    /**
     * 成员是否第一次进入学习圈
     */
    LEARNING_CIRCLE_DRAFT_TOPIC = 'LEARNING_CIRCLE_DRAFT_TOPIC',

    ACTIVITY_ANNUAL_2019_BGM_PLAYING = 'ACTIVITY_ANNUAL_2019_BGM_PLAYING',

    ACTIVITY_ANNUAL_2020_SHARE_BG = 'ACTIVITY_ANNUAL_2020_SHARE_BG__',

    ACTIVITY_ANNUAL_2020_THEME_TEXT = 'ACTIVITY_ANNUAL_2020_THEME_TEXT__',

    /** 2021年度报告 */
    ACTIVITY_ANNUAL_2021_BGM_PLAYING = 'ACTIVITY_ANNUAL_2021_BGM_PLAYING',
    ACTIVITY_ANNUAL_2021_SHARE_BG = 'ACTIVITY_ANNUAL_2021_SHARE_BG_',

    /**
     * 存储小节学习时长
     */
    CONTENT_SESSION_LEARNING_TIME = 'CONTENT_SESSION_LEARNING_TIME',
}

export enum STORAGE_VALUE {
    TRUE = '1',
    FALSE = '0',
}

/**
 * data persistent in browser，localStorage with cookie fallback
 * usage:
 * storageHelper.set('foo','bar')
 * storageHelper.get('foo') //bar
 * http://stackoverflow.com/a/12302790/1553656
 */
export const localStorageHelper = {
    localStoreSupport() {
        try {
            const isSupport = 'localStorage' in window && window.localStorage !== null;
            if (isSupport) {
                // safari隐身模式里边也不能用
                localStorage.setItem('__test', '1');
                localStorage.removeItem('__test');
            }
            return isSupport;
        } catch (e) {
            return false;
        }
    },
    set(name: STORAGE_KEY | string, value: string, days?: number) {
        let expires = '';
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = '; expires=' + date.toUTCString();
        }
        if (this.localStoreSupport()) {
            localStorage.setItem(name, value);
        } else {
            document.cookie = name + '=' + value + expires + '; path=/';
        }
    },
    get(name: STORAGE_KEY | string) {
        if (this.localStoreSupport()) {
            return localStorage.getItem(name);
        } else {
            return cookieHelper.get(name);
        }
    },
    del(name: STORAGE_KEY | string) {
        if (this.localStoreSupport()) {
            localStorage.removeItem(name);
        } else {
            this.set(name, '', -1);
        }
    },
};

/**
 * 功能和local storage类似,
 * 移动端兼容效果较好
 * 隐身模式下和local storage一样
 */
export const sessionStorageHelper = {
    sessionStoreSupport() {
        try {
            const isSupport = 'sessionStorage' in window && window.sessionStorage !== null;
            if (isSupport) {
                // safari隐身模式里边也不能用
                sessionStorage.setItem('__test', '1');
                sessionStorage.removeItem('__test');
            }
            return isSupport;
        } catch (e) {
            return false;
        }
    },
    set(name: STORAGE_KEY | string, value: string, days: number = 1) {
        if (this.sessionStoreSupport()) {
            try {
                sessionStorage.setItem(name, value);
            } catch (error: any) {
                const err = new Error();
                err.stack = error.stack;
                err.message = 'set sessionStorage error';
                throw err;
            }
        } else {
            cookieHelper.set(name, value, days);
        }
    },
    get(name: STORAGE_KEY | string) {
        if (this.sessionStoreSupport()) {
            return sessionStorage.getItem(name);
        } else {
            return cookieHelper.get(name);
        }
    },
    del(name: STORAGE_KEY | string) {
        if (this.sessionStoreSupport()) {
            sessionStorage.removeItem(name);
        } else {
            this.set(name, '', -1);
        }
    },
};

/**
 * 获取和设置cookie值
 */
export const cookieHelper = {
    set(name: STORAGE_KEY | string, value: string, days: number = 1) {
        let expires = '';
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + value + expires + '; path=/';
    },
    get(name: STORAGE_KEY | string) {
        const nameEQ = name + '=';
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1, c.length);
            }
            if (c.indexOf(nameEQ) === 0) {
                return c.substring(nameEQ.length, c.length);
            }
        }
        return null;
    },
    del(name: STORAGE_KEY | string) {
        this.set(name, '', -1);
    },
};
